import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import {
  Memory as MemoryIcon,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import type { MemoryReport } from '@/types';

interface MemoryMonitorProps {
  memoryReports: MemoryReport[];
  isLive?: boolean;
  // Kept so existing callers don't need to change; no longer rendered.
  threshold?: number;
  onClear?: () => void;
  height?: number;
}

const CHART_COLOR = '#1976D2';

// Gap / stall threshold: a sample counts as "missing" once this long passes
// with no new frame (~2.5x the nominal 5s interval). Any gap longer than this —
// mid-stream or an ongoing stall — is drawn as 0 MB.
const GAP_MS = 13_000;
const STALL_NOTE_MS = 30_000; // after a sustained stall, hint the device may have restarted
const LIVE_WINDOW_MS = 5 * 60 * 1000;
const TICK_STEP_MS = 60_000; // label ticks at whole-minute marks

type Pt = {
  timestampMs: number;
  usedMb: number | null;
  totalMb: number | null;
  freeMb: number | null;
  availableMb: number | null;
  timestamp: string;
};

const zeroPt = (ts: number): Pt => ({
  timestampMs: ts,
  usedMb: 0,
  totalMb: null,
  freeMb: null,
  availableMb: null,
  timestamp: '',
});

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

// Axis labels sit at whole-minute marks, so drop the seconds for a cleaner,
// steadier axis. The tooltip still shows full HH:MM:SS via formatTime.
function formatAxisTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function MemoryMonitor({
  memoryReports,
  isLive = false,
  height = 300,
}: MemoryMonitorProps) {
  // Transform data for chart (KB to MB for better visualization)
  const chartData = useMemo(() => {
    return memoryReports.map((report) => ({
      timestampMs: new Date(report.timestamp).getTime(),
      usedMb: report.usedKb / 1024,
      totalMb: report.totalKb / 1024,
      freeMb: report.freeKb / 1024,
      availableMb: report.availableKb / 1024,
      timestamp: report.timestamp,
    }));
  }, [memoryReports]);

  const lastSampleMs = chartData.length > 0 ? chartData[chartData.length - 1].timestampMs : null;

  // ── Live clock ──────────────────────────────────────────────────────
  // A ticking clock lets the chart keep advancing — and draw 0 during a stall —
  // even when the device sends nothing (an idle device produces no re-render).
  // The monitor never pauses; a silent device simply reads 0.
  const [nowTick, setNowTick] = useState<number>(() => Date.now());
  const [lastReceivedAt, setLastReceivedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!isLive) return;
    setNowTick(Date.now());
    const id = setInterval(() => setNowTick(Date.now()), 3000);
    return () => clearInterval(id);
  }, [isLive]);

  // Stamp the browser arrival time of the latest sample (its timestamp changing
  // is the reliable "a new frame arrived" signal). Used to estimate the device
  // clock, so the wall-clock math stays aligned with the device-timestamp x-axis
  // even under device/browser clock skew.
  useEffect(() => {
    if (lastSampleMs != null) setLastReceivedAt(Date.now());
  }, [lastSampleMs]);

  // "Now" in the device's clock: correct the browser clock by the offset seen at
  // the last sample. Keeps wall-clock advancement on the same axis as the data.
  const clockOffset = lastReceivedAt != null && lastSampleMs != null ? lastReceivedAt - lastSampleMs : 0;
  const deviceNow = nowTick - clockOffset;

  // On Stop, freeze "now" at the stop moment so the view stays EXACTLY as shown
  // — including a 0-line that was in progress during a stall (we don't drop it).
  // Null while streaming, and null if the monitor was never live (viewing old
  // data), so a stale last sample doesn't paint a giant 0 tail on load.
  const [frozenNow, setFrozenNow] = useState<number | null>(null);
  const wasLiveRef = useRef(isLive);
  useEffect(() => {
    if (wasLiveRef.current && !isLive) {
      setFrozenNow(Date.now() - clockOffset); // just stopped
    } else if (isLive) {
      setFrozenNow(null); // resumed
    }
    wasLiveRef.current = isLive;
  }, [isLive, clockOffset]);

  // "Now" for stall math: the live clock while streaming, else the frozen
  // stop-moment (or null when there is no frozen live view).
  const effNow = isLive ? deviceNow : frozenNow;

  // Stalled = has data and no fresh sample for longer than GAP_MS, measured
  // against effNow (frozen when stopped, so the 0 segment stays put).
  const stalled = lastSampleMs != null && effNow != null && effNow - lastSampleMs > GAP_MS;
  // Sustained stall — long enough that the device likely restarted/rebooted
  // (which ends the live stream); surfaces the info note.
  const sustainedStall = isLive && lastSampleMs != null && deviceNow - lastSampleMs > STALL_NOTE_MS;

  const xDomain = useMemo<[number, number] | undefined>(() => {
    // Right edge = the stall front (0-line tip) while stalled, else the last
    // real sample. effNow is frozen on Stop, so clicking Stop freezes the view
    // exactly — including any in-progress 0 segment — without re-fitting.
    if (lastSampleMs == null && effNow == null) return undefined;
    const end = stalled ? (effNow as number) : (lastSampleMs ?? (effNow as number));
    return [end - LIVE_WINDOW_MS, end];
  }, [stalled, lastSampleMs, effNow]);

  // Tick marks at whole-minute boundaries, so labels sit at steady round times.
  const xTicks = useMemo<number[] | undefined>(() => {
    if (!xDomain) return undefined;
    const [start, end] = xDomain;
    const ticks: number[] = [];
    for (let t = Math.ceil(start / TICK_STEP_MS) * TICK_STEP_MS; t <= end; t += TICK_STEP_MS) {
      ticks.push(t);
    }
    return ticks.length > 0 ? ticks : undefined;
  }, [xDomain]);

  // Plotted series: real samples with the value dropped to 0 across any gap
  // longer than GAP_MS — both mid-stream gaps (between two samples) and an
  // ongoing stall (last sample → now). (Reads as memory hitting 0, which isn't
  // literally true — the device just stopped reporting during the gap.)
  const series = useMemo(() => {
    const out: Pt[] = [];
    for (let i = 0; i < chartData.length; i++) {
      const cur = chartData[i];
      if (i > 0 && cur.timestampMs - chartData[i - 1].timestampMs > GAP_MS) {
        out.push(zeroPt(chartData[i - 1].timestampMs + 1));
        out.push(zeroPt(cur.timestampMs - 1));
      }
      out.push(cur);
    }
    // Stall: extend a 0-line from just after the last sample to the stall front
    // (effNow). Frozen on Stop, so the 0 segment is kept, not dropped.
    if (stalled && lastSampleMs != null && effNow != null) {
      out.push(zeroPt(lastSampleMs + 1));
      out.push(zeroPt(effNow));
    }
    return out;
  }, [chartData, stalled, lastSampleMs, effNow]);

  const showLivePulse = isLive && memoryReports.length > 0;

  return (
    <Box>
      {/* Chart */}
      <Paper variant="outlined" sx={{ p: 2, position: 'relative' }}>
        {memoryReports.length === 0 ? (
          <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Box sx={{ textAlign: 'center', color: '#9E9E9E' }}>
              <MemoryIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
              <Typography>
                {isLive ? 'Waiting for memory reports...' : 'No memory data recorded'}
              </Typography>
              <Typography variant="caption">
                {isLive ? 'Data will appear as reports arrive' : 'Start monitoring to collect data'}
              </Typography>
            </Box>
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart data={series} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorUsed" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLOR} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E6E6" />
              <XAxis
                dataKey="timestampMs"
                type="number"
                scale="time"
                domain={xDomain ?? ['dataMin', 'dataMax']}
                ticks={xTicks}
                tick={{ fontSize: 11, fill: '#666666' }}
                tickLine={false}
                tickFormatter={(ts: number) => formatAxisTime(ts)}
                allowDataOverflow
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#666666' }}
                tickLine={false}
                tickFormatter={(value) => `${value.toFixed(0)} MB`}
                domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.15)]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E6E6E6',
                  borderRadius: 4,
                  fontSize: 12,
                }}
                formatter={(value: number, name: string) => [
                  `${value.toFixed(1)} MB`,
                  name === 'usedMb' ? 'Used' : name === 'totalMb' ? 'Total' : name,
                ]}
                labelFormatter={(label) => `Time: ${formatTime(new Date(label as number).toISOString())}`}
              />
              {/* Used memory area. Gaps longer than GAP_MS drop to 0 MB (zero
                  points inserted in `series`), so a stall reads as a trough. */}
              <Area
                type="monotone"
                dataKey="usedMb"
                stroke={CHART_COLOR}
                strokeWidth={2}
                fill="url(#colorUsed)"
                name="Used"
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        )}

        {/* Live pulse — a glanceable "streaming" cue while active. */}
        {showLivePulse && (
          <Box sx={{ position: 'absolute', top: 12, right: 16, display: 'flex', alignItems: 'center', gap: 0.5, pointerEvents: 'none' }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main',
                '@keyframes memLivePulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
                animation: 'memLivePulse 1.6s ease-in-out infinite',
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            />
            <Typography sx={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: 'success.main', letterSpacing: '0.06em' }}>
              LIVE
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Sustained-stall hint — the value has read 0 for a while. */}
      {sustainedStall && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No memory readings for a while — the device may have restarted or rebooted, which ends the
          live stream. Press <strong>Stop</strong>, then <strong>Start Monitor</strong> again to resume.
        </Alert>
      )}

      {/* Legend */}
      {memoryReports.length > 0 && (
        <Box sx={{ display: 'flex', gap: 3, mt: 1, justifyContent: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 16, height: 3, bgcolor: CHART_COLOR, borderRadius: 1 }} />
            <Typography variant="caption" color="text.secondary">Used Memory</Typography>
          </Box>
        </Box>
      )}

      {/* Window cap hint — the FE buffer + sliding domain only hold ~5 minutes. */}
      {isLive && (
        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 0.5, color: 'text.disabled', fontSize: 11 }}
        >
          Showing the last 5 minutes of live samples.
        </Typography>
      )}
    </Box>
  );
}

export default MemoryMonitor;
